import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Eye, EyeOff, MoreHorizontal, Search } from "lucide-react";
import * as React from "react";

import type { ClusterOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Sparkline } from "@/widgets/Sparkline";
import { TempBadge } from "@/widgets/TempBadge";

const INTENT_LABEL: Record<string, string> = {
  info: "инфо",
  commercial: "коммерч.",
  navigational: "навиг.",
};

const INTENT_BADGE: Record<string, string> = {
  info: "bg-purple/12 border-purple/30 text-purple",
  commercial: "bg-green/12 border-green/30 text-green",
  navigational: "bg-white/[0.05] border-white/15 text-muted-foreground",
};

const PRIORITY_BADGE: Record<string, string> = {
  P0: "bg-pink/15 border-pink/45 text-pink",
  P1: "bg-amber/12 border-amber/35 text-amber",
  P2: "bg-white/[0.05] border-white/15 text-muted-foreground",
};

export interface ProjectClustersTableProps {
  clusters: ClusterOut[];
  onRowClick?: (cluster: ClusterOut) => void;
  onToggleHidden?: (cluster: ClusterOut) => void;
  showHidden?: boolean;
  onShowHiddenChange?: (value: boolean) => void;
  /** Сколько кластеров суммарно скрыто — для лейбла «скрытые (N)». */
  hiddenCount?: number;
}

export function ProjectClustersTable({
  clusters,
  onRowClick,
  onToggleHidden,
  showHidden = false,
  onShowHiddenChange,
  hiddenCount = 0,
}: ProjectClustersTableProps): JSX.Element {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "total_count", desc: true },
  ]);
  const [search, setSearch] = React.useState("");
  const [intentFilter, setIntentFilter] = React.useState<string>("all");
  const [priorityFilter, setPriorityFilter] = React.useState<string>("all");
  const [tempFilter, setTempFilter] = React.useState<string>("all");

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return clusters.filter((c) => {
      if (q && !c.main_phrase.toLowerCase().includes(q)) return false;
      if (intentFilter !== "all" && c.intent !== intentFilter) return false;
      if (priorityFilter !== "all" && c.priority !== priorityFilter) return false;
      if (tempFilter !== "all" && (c.temperature ?? "unknown") !== tempFilter)
        return false;
      return true;
    });
  }, [clusters, search, intentFilter, priorityFilter, tempFilter]);

  const columns = React.useMemo<ColumnDef<ClusterOut>[]>(
    () => [
      {
        accessorKey: "idx",
        header: "#",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.idx}
          </span>
        ),
        size: 50,
      },
      {
        accessorKey: "main_phrase",
        header: "Главный запрос",
        cell: ({ row }) => (
          <span className="font-medium text-foreground">
            {row.original.main_phrase}
          </span>
        ),
      },
      {
        accessorKey: "intent",
        header: "Интент",
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex rounded-md border px-2 py-0.5 text-[11px] font-bold",
              INTENT_BADGE[row.original.intent] ?? INTENT_BADGE.info,
            )}
          >
            {INTENT_LABEL[row.original.intent] ?? row.original.intent}
          </span>
        ),
        enableSorting: false,
        size: 110,
      },
      {
        accessorKey: "priority",
        header: "Приор.",
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex rounded-md border px-2 py-0.5 text-[11px] font-bold",
              PRIORITY_BADGE[row.original.priority] ?? PRIORITY_BADGE.P2,
            )}
          >
            {row.original.priority}
          </span>
        ),
        size: 80,
      },
      {
        id: "temperature",
        header: "Темп.",
        cell: ({ row }) => (
          <TempBadge
            heat={{
              temperature: row.original.temperature,
              trend_pct: row.original.trend_pct,
            }}
          />
        ),
        enableSorting: false,
        size: 100,
      },
      {
        id: "sparkline",
        header: "Динамика",
        cell: ({ row }) => (
          <span className="text-cyan">
            <Sparkline data={row.original.sparkline ?? []} />
          </span>
        ),
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "total_count",
        header: () => (
          <div
            className="text-right"
            title="Сумма базовых частот всех фраз кластера. Главная фраза показывает частоту в кавычках по hover."
          >
            Частота
          </div>
        ),
        cell: ({ row }) => (
          <div
            className="text-right font-mono tabular-nums"
            title={
              `базовая (сумма по кластеру): ${row.original.total_count.toLocaleString("ru-RU")}` +
              `\nглавная фраза «${row.original.main_phrase}»: ${row.original.main_count.toLocaleString("ru-RU")}`
            }
          >
            {formatNumber(row.original.total_count)}
          </div>
        ),
        size: 110,
      },
      {
        accessorKey: "size",
        header: () => <div className="text-right">Фраз</div>,
        cell: ({ row }) => (
          <div className="text-right font-mono tabular-nums text-muted-foreground">
            {row.original.size}
          </div>
        ),
        size: 80,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <ClusterRowMenu
            cluster={row.original}
            onToggleHidden={onToggleHidden}
          />
        ),
        enableSorting: false,
        size: 40,
      },
    ],
    [onToggleHidden],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по главному запросу…"
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Select value={intentFilter} onValueChange={setIntentFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Интент" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все интенты</SelectItem>
            <SelectItem value="info">инфо</SelectItem>
            <SelectItem value="commercial">коммерч.</SelectItem>
            <SelectItem value="navigational">навиг.</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Приоритет" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Любой</SelectItem>
            <SelectItem value="P0">P0</SelectItem>
            <SelectItem value="P1">P1</SelectItem>
            <SelectItem value="P2">P2</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tempFilter} onValueChange={setTempFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Температура" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Любая</SelectItem>
            <SelectItem value="hot">🔥 горячие</SelectItem>
            <SelectItem value="rising">📈 растущие</SelectItem>
            <SelectItem value="stable">🟢 стабильные</SelectItem>
            <SelectItem value="cooling">📉 остывающие</SelectItem>
            <SelectItem value="frozen">❄️ замёрзшие</SelectItem>
            <SelectItem value="unknown">❓ нет данных</SelectItem>
          </SelectContent>
        </Select>
        {onShowHiddenChange && (
          <button
            type="button"
            onClick={() => onShowHiddenChange(!showHidden)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs",
              showHidden
                ? "border-purple/45 bg-purple/[0.08] text-purple"
                : "border-white/15 bg-white/[0.04] text-muted-foreground hover:text-white",
            )}
            title={
              showHidden ? "Спрятать скрытые обратно" : "Показать скрытые кластеры"
            }
          >
            {showHidden ? (
              <Eye className="h-3.5 w-3.5" />
            ) : (
              <EyeOff className="h-3.5 w-3.5" />
            )}
            {showHidden ? "скрытые видны" : `скрытые${hiddenCount ? ` (${hiddenCount})` : ""}`}
          </button>
        )}
      </div>

      <div className="glass-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white/[0.04] backdrop-blur">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-white/10">
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.column.columnDef.size }}
                    className={cn(
                      "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                      h.column.getCanSort() && "cursor-pointer select-none hover:text-white",
                    )}
                    onClick={
                      h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined
                    }
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {h.column.getCanSort() && (
                      <span className="ml-1 text-[10px] text-muted-foreground/50">
                        {h.column.getIsSorted() === "asc"
                          ? "↑"
                          : h.column.getIsSorted() === "desc"
                            ? "↓"
                            : ""}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={cn(
                  "border-b border-white/10",
                  onRowClick && "cursor-pointer hover:bg-white/[0.04]",
                  row.original.is_hidden && "opacity-50",
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  Ничего не найдено
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground">
        Показано {table.getRowModel().rows.length} из {clusters.length}
      </div>
    </div>
  );
}


function ClusterRowMenu({
  cluster,
  onToggleHidden,
}: {
  cluster: ClusterOut;
  onToggleHidden?: (cluster: ClusterOut) => void;
}): JSX.Element | null {
  if (!onToggleHidden) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Меню кластера"
          onClick={(e) => e.stopPropagation()}
          className="rounded p-1 text-muted-foreground hover:bg-white/[0.06] hover:text-white"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          onSelect={() => onToggleHidden(cluster)}
          className={cluster.is_hidden ? "" : "text-pink focus:text-pink"}
        >
          {cluster.is_hidden ? (
            <>
              <Eye className="h-4 w-4" /> Вернуть кластер
            </>
          ) : (
            <>
              <EyeOff className="h-4 w-4" /> Скрыть кластер
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
